import {
  DEFAULT_ECONOMICS,
  DEFAULT_STEP_MODE,
  OVERRIDE_KEYS,
} from './config.js';
import { costPerOccurrence, annualize } from './cost.js';

// Pull the per-step config out of the backend shape:
// value_story_config.steps = { "<index>": { failure_mode, overrides: {...} } }
function stepConfigFor(taskConfig, stepIndex) {
  const entry = taskConfig?.steps?.[stepIndex] ?? taskConfig?.steps?.[String(stepIndex)];
  if (!entry) return { mode: null, overrides: {} };
  const overrides = {};
  const raw = entry.overrides;
  if (raw && typeof raw === 'object') {
    for (const key of OVERRIDE_KEYS) {
      const v = raw[key];
      if (v == null || v === '') continue;
      const n = Number(v);
      if (Number.isFinite(n)) overrides[key] = n;
    }
  }
  return {
    mode: entry.failure_mode || null,
    overrides,
  };
}

// Pure port of the enrichedSteps computed from the ACP dashboard prototype.
// Inputs:
//   steps       — [{ index, name, interventions?, missed? }, ...]
//   taskConfig  — value_story_config: { steps: {...}, economics: {...} }
// Output:
//   [{ ...step, mode, overrides, hasCustomization, perOccurrenceCost,
//      costIfFailed, totalCost }, ...]
export function enrichSteps(steps, taskConfig) {
  if (!Array.isArray(steps)) return [];
  const economics = { ...DEFAULT_ECONOMICS, ...(taskConfig?.economics || {}) };
  return steps.map((step) => {
    const { mode: rawMode, overrides } = stepConfigFor(taskConfig, step.index);
    const mode = rawMode || DEFAULT_STEP_MODE;
    const effEcon = { ...economics, ...overrides };
    const perOccurrence = costPerOccurrence(mode, effEcon);
    const interventions = step.interventions || 0;
    return {
      ...step,
      mode,
      overrides,
      hasCustomization: Object.keys(overrides).length > 0,
      perOccurrenceCost: perOccurrence,
      costIfFailed: perOccurrence,
      totalCost: perOccurrence * interventions,
    };
  });
}

// Aggregate enriched steps into per-failure-mode buckets, sorted by total desc.
export function breakdownByMode(enriched) {
  const buckets = new Map();
  for (const step of enriched) {
    if (!buckets.has(step.mode)) buckets.set(step.mode, { count: 0, total: 0 });
    const b = buckets.get(step.mode);
    b.count += step.interventions || 0;
    b.total += step.totalCost;
  }
  return Array.from(buckets.entries())
    .filter(([, v]) => v.count > 0 || v.total > 0)
    .map(([mode, v]) => ({
      mode,
      count: v.count,
      total: v.total,
      avgPerOccurrence: v.count > 0 ? v.total / v.count : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

export function rankStepsByValue(enriched) {
  return [...enriched].sort(
    (a, b) => b.totalCost - a.totalCost || b.costIfFailed - a.costIfFailed
  );
}

export function sumWindowTotal(enriched) {
  return enriched.reduce((sum, s) => sum + s.totalCost, 0);
}

export function totalInterventions(steps) {
  if (!Array.isArray(steps)) return 0;
  return steps.reduce((s, step) => s + (step.interventions || 0), 0);
}

// Rough average per-occurrence cost across all configured steps in a task.
// Used on surfaces that only have aggregate bad-cycle counts (e.g. the
// device dashboard) and need a single directional dollar figure. Falls back
// to the default failure mode's cost when a step isn't explicitly configured.
export function averagePerOccurrenceCost(taskConfig) {
  if (!taskConfig) return 0;
  const economics = { ...DEFAULT_ECONOMICS, ...(taskConfig.economics || {}) };
  const stepEntries = Object.values(taskConfig.steps || {});
  const modes = stepEntries.length
    ? stepEntries.map((entry) => ({
        mode: entry?.failure_mode || DEFAULT_STEP_MODE,
        overrides:
          entry?.overrides && typeof entry.overrides === 'object'
            ? entry.overrides
            : {},
      }))
    : [{ mode: DEFAULT_STEP_MODE, overrides: {} }];
  const sum = modes.reduce((acc, { mode, overrides }) => {
    const eff = { ...economics, ...overrides };
    return acc + costPerOccurrence(mode, eff);
  }, 0);
  return sum / modes.length;
}

// Rough "cost avoided" figure for surfaces without per-step intervention
// data — multiplies bad-cycle count by the task's average per-occurrence
// cost. Directionally accurate; not per-step precise.
export function roughCostAvoided(taskConfig, badCycles) {
  const n = Number(badCycles);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return averagePerOccurrenceCost(taskConfig) * n;
}

// Convenience wrapper used by widgets. Given raw inputs, returns the fully
// derived numbers so the widget stays thin.
export function computeValueStory({
  steps,
  taskConfig,
  windowDays,
}) {
  const enriched = enrichSteps(steps, taskConfig);
  const windowTotal = sumWindowTotal(enriched);
  const operatingDays =
    taskConfig?.economics?.operatingDaysPerYear ??
    DEFAULT_ECONOMICS.operatingDaysPerYear;
  return {
    enriched,
    windowTotal,
    annual: annualize(windowTotal, windowDays, operatingDays),
    breakdown: breakdownByMode(enriched),
    ranked: rankStepsByValue(enriched),
    totalInterventions: totalInterventions(steps),
  };
}
